/**
 * This is a MinMax Component.This Component can be used in the Filter for filter the Values based on the Min value and the Max Value.
 *
 * @module views/components/MinMax
 * @memberof -Common
 */
import './MinMax.scss';

import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import Text from '@ulta/core/components/Text/Text';

import * as utils from './MinMax';

/**
 * @method handleApplyFilters
 * @summary Method to submit Price range values
 * @param {object} methods - passing setMinMaxValues, onApplyFiltersClick functions
 * @param {object} data - passing values data
 */
export const handleApplyFilters = ( methods, data ) => () => {
  const { setMinMaxValues, onApplyFiltersClick } = methods;
  const { values } = data;

  const inputValues = { ...values };

  if( inputValues.min === '' || inputValues.min === null ){
    inputValues.min = '0';
  }

  if( inputValues.max === '' ){
    inputValues.max = '9999';
  }

  if( +inputValues.min > +inputValues.max ){
    const tempValue = inputValues.max;
    inputValues.max = inputValues.min;
    inputValues.min = tempValue;

    setMinMaxValues( { min: inputValues.min, max: inputValues.max } );
  }

  onApplyFiltersClick( inputValues );
  setMinMaxValues( { min: inputValues.min, max: inputValues.max } );
};

/**
 * Represents a MinMax component
 *
 * @method
 * @param {MinMaxProps} props - React properties passed from composition
 * @returns MinMax
 */
export const MinMax = React.forwardRef( function( props, ref ){
  const {
    label,
    labelPositionLeft = defaultProps.labelPositionLeft,
    minPlaceholder,
    maxPlaceholder,
    onApplyFiltersClick,
    selectAction,
    minAmount,
    maxAmount
  } = props;

  const [isSubmitDisabled, setSubmitDisable] = useState( true );
  let minVal = '';
  if( maxAmount > 0 && ( minAmount === '' || minAmount === null ) ){
    minVal = '0';
  }
  const [minMaxValues, setMinMaxValues] = useState( {
    min: minAmount || minVal,
    max: maxAmount || ''
  } );

  const onValuesChange = event => {
    setMinMaxValues( {
      ...minMaxValues,
      [event.target.name]: event.target.value.replace( /[^0-9]/g, '' )
    } );
  };

  useEffect( () => {
    if( minMaxValues.min === '' && minMaxValues.max === '' ){
      setSubmitDisable( true );
    }
    else {
      setSubmitDisable( false );
    }
  }, [minMaxValues.min, minMaxValues.max] );

  return (
    <div className='MinMax'>
      <div className='MinMax__input'>
        { label && labelPositionLeft &&
        <div className='MinMax__label'>
          <Text>
            { label }
          </Text>
        </div>
        }
        <label
          className='sr-only'
          htmlFor='priceMin'
        >minimum amount to spend
        </label>
        <input
          ref={ ref }
          id='priceMin'
          name='min'
          placeholder={ minPlaceholder }
          value={ minMaxValues.min }
          onChange={ onValuesChange }
          type='tel'
          maxLength='4'
        />
        { label && !labelPositionLeft &&
        <div className='MinMax__label'>
          <Text>
            { label }
          </Text>
        </div>
        }
      </div>

      <div className='MinMax__input'>
        { label && labelPositionLeft &&
        <div className='MinMax__label'>
          <Text>
            { label }
          </Text>
        </div>
        }
        <label
          className='sr-only'
          htmlFor='priceMax'
        >maximum amount to spend</label>
        <input
          id='priceMax'
          name='max'
          placeholder={ maxPlaceholder }
          value={ minMaxValues.max }
          onChange={ onValuesChange }
          type='tel'
          maxLength='4'
        />
        { label && !labelPositionLeft &&
        <div className='MinMax__label'>
          <Text>
            { label }
          </Text>
        </div>
        }
      </div>

      <div className='MinMax__ArrowForward'>
        <Button
          className='MinMax__button'
          variant='unstyled'
          type='submit'
          iconImage={ 'ArrowForward' }
          icon={ true }
          iconSize={ 'm' }
          onClick={ utils.handleApplyFilters(
            {
              setMinMaxValues,
              onApplyFiltersClick
            },
            {
              values: minMaxValues
            } ) }
          disabled={ isSubmitDisabled }
          ariaLabel={ 'click to apply spend range filter' }
          action={ selectAction }
        />
      </div>
    </div>
  );
} );

MinMax.displayName = 'MinMax';

/**
 * Property type definitions
 *
 * @typedef MinMaxProps
 * @type {object}
 * @property {string} label - sets the label for the minmax filter
 * @property {string} minPlaceholder - sets the placeholder for the minfilter
 * @property {string} maxPlaceholder - sets the placeholder for the max filter
 * @property {boolean} labelPositionLeft - Sets the position of the label
 */
export const propTypes = {
  /** sets the label for the minmax filter */
  label: PropTypes.string,
  /** sets the placeholder for the minfilter */
  minPlaceholder: PropTypes.string,
  /** sets the placeholder for the max filter */
  maxPlaceholder: PropTypes.string,
  /** Sets the label to left side if true */
  labelPositionLeft: PropTypes.bool
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {boolean} labelPositionLeft=false - Sets the position of label to left side.
 */
export const defaultProps = {
  labelPositionLeft: true
};

MinMax.propTypes = propTypes;
MinMax.defaultProps = defaultProps;

export default MinMax;
